import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { useComplaintModal } from "../context/ComplaintModalContext";

// GERADOR DE COBRANÇA (19/09/2026). A melhor ideia de uma conversa do Nelson com uma IA externa: o candidato
// que ficou sem resposta escreve aqui, em 30 segundos, um e-mail educado cobrando o retorno — e o eaiRH ajuda
// na hora em que a pessoa mais precisa. Três ganhos: resolve o problema dela (muitas empresas respondem quando
// cobradas), leva o nome eaiRH até o recrutador (assinatura opcional, ligada por padrão, que ela pode tirar)
// e, se mesmo assim não houver resposta, o caminho natural é registrar o relato — que é o que falta ao portal.
// Tudo acontece no navegador: nada do que a pessoa digita aqui é enviado ao servidor.

const ETAPAS = [
  { v: "inscricao", t: "Me inscrevi / mandei o currículo" },
  { v: "entrevista", t: "Fiz entrevista" },
  { v: "teste", t: "Fiz teste ou desafio técnico" },
  { v: "final", t: "Fiz a etapa final" },
];

const FRASE_ETAPA = {
  inscricao: "me candidatei à vaga",
  entrevista: "participei da entrevista para a vaga",
  teste: "entreguei o teste/desafio da vaga",
  final: "participei da etapa final do processo para a vaga",
};

function montar({ nome, recrutador, empresa, vaga, etapa, quando, tom, assinatura }) {
  const saud = recrutador.trim() ? `Olá, ${recrutador.trim()}, tudo bem?` : "Olá, tudo bem?";
  const vagaTxt = vaga.trim() || "[nome da vaga]";
  const empTxt = empresa.trim() || "[empresa]";
  const quandoTxt = quando.trim() ? ` em ${quando.trim()}` : "";
  const assunto = `Retorno sobre o processo seletivo — ${vagaTxt}`;
  const corpoFormal =
    `${saud}\n\n${FRASE_ETAPA[etapa].charAt(0).toUpperCase() + FRASE_ETAPA[etapa].slice(1)} de ${vagaTxt} na ${empTxt}${quandoTxt} e gostaria de saber em que pé está o processo.\n\n` +
    `Sigo com interesse na oportunidade e fico à disposição para qualquer informação adicional. Caso o processo já tenha sido encerrado para mim, ` +
    `um breve retorno também me ajuda muito a me organizar.\n\nAgradeço desde já.\n\n${nome.trim() || "[seu nome]"}`;
  const corpoLeve =
    `${saud}\n\nPassando para saber como está o processo da vaga de ${vagaTxt} na ${empTxt} — ${FRASE_ETAPA[etapa]}${quandoTxt}.\n\n` +
    `Continuo com interesse! E se eu não tiver seguido no processo, sem problema: um retorno rápido já me ajuda a me organizar.\n\n` +
    `Obrigado(a)!\n${nome.trim() || "[seu nome]"}`;
  let corpo = tom === "leve" ? corpoLeve : corpoFormal;
  if (assinatura) corpo += "\n\n—\nMensagem escrita com a ajuda do eaiRH (eairh.com.br), onde candidatos registram como foi cada processo seletivo.";
  return { assunto, corpo };
}

export default function CobrarRetornoPage() {
  const { openComplaint } = useComplaintModal();
  const [f, setF] = useState({ nome: "", recrutador: "", emailRecrutador: "", empresa: "", vaga: "", etapa: "entrevista", quando: "", tom: "formal", assinatura: true });
  const [copiado, setCopiado] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const { assunto, corpo } = useMemo(() => montar(f), [f]);
  const mailto = `mailto:${encodeURIComponent(f.emailRecrutador.trim())}?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`;
  const copiar = () => {
    try { navigator.clipboard.writeText(`Assunto: ${assunto}\n\n${corpo}`); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* sem clipboard */ }
  };
  const inputCls = "w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <span className="inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">Ferramenta gratuita</span>
        <h1 className="mt-3 text-3xl md:text-4xl font-bold text-text">Como cobrar retorno de um processo seletivo</h1>
        <p className="mt-3 text-gray-600">
          Fez a entrevista e a empresa sumiu? Preencha abaixo e leve um e-mail educado, pronto para mandar ao recrutador.
          Muitas empresas respondem quando são lembradas. Nada do que você digita aqui sai do seu navegador.
        </p>

        <section className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div><label className={labelCls} htmlFor="cr-vaga">Vaga</label>
            <input id="cr-vaga" className={inputCls} value={f.vaga} onChange={set("vaga")} placeholder="Ex.: Analista de Suporte" /></div>
          <div><label className={labelCls} htmlFor="cr-empresa">Empresa</label>
            <input id="cr-empresa" className={inputCls} value={f.empresa} onChange={set("empresa")} placeholder="Ex.: Empresa X" /></div>
          <div><label className={labelCls} htmlFor="cr-etapa">Até onde você foi</label>
            <select id="cr-etapa" className={inputCls} value={f.etapa} onChange={set("etapa")}>
              {ETAPAS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
            </select></div>
          <div><label className={labelCls} htmlFor="cr-quando">Quando foi <span className="font-normal text-gray-400">(opcional)</span></label>
            <input id="cr-quando" className={inputCls} value={f.quando} onChange={set("quando")} placeholder="Ex.: 10/09" /></div>
          <div><label className={labelCls} htmlFor="cr-recrutador">Nome de quem te atendeu <span className="font-normal text-gray-400">(opcional)</span></label>
            <input id="cr-recrutador" className={inputCls} value={f.recrutador} onChange={set("recrutador")} placeholder="Ex.: Mariana" /></div>
          <div><label className={labelCls} htmlFor="cr-email">E-mail de quem te atendeu <span className="font-normal text-gray-400">(opcional)</span></label>
            <input id="cr-email" type="email" className={inputCls} value={f.emailRecrutador} onChange={set("emailRecrutador")} placeholder="Para abrir direto no seu e-mail" /></div>
          <div><label className={labelCls} htmlFor="cr-nome">Seu nome</label>
            <input id="cr-nome" className={inputCls} value={f.nome} onChange={set("nome")} placeholder="Como você assina" /></div>
          <div><label className={labelCls} htmlFor="cr-tom">Tom</label>
            <select id="cr-tom" className={inputCls} value={f.tom} onChange={set("tom")}>
              <option value="formal">Mais formal</option>
              <option value="leve">Mais leve</option>
            </select></div>
          <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={f.assinatura} onChange={set("assinatura")} />
            Incluir no fim que a mensagem foi escrita com a ajuda do eaiRH
          </label>
        </section>

        <section className="mt-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
            <h2 className="font-semibold text-gray-900">Seu e-mail pronto</h2>
            <div className="flex gap-2">
              <button type="button" onClick={copiar} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                {copiado ? "Copiado ✓" : "Copiar"}
              </button>
              <a href={mailto} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90">Abrir no meu e-mail</a>
            </div>
          </div>
          <div className="px-5 py-4">
            <p className="text-sm text-gray-500"><b>Assunto:</b> {assunto}</p>
            <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">{corpo}</pre>
          </div>
        </section>

        <section className="mt-8 rounded-2xl bg-text text-white p-6">
          <h2 className="text-xl font-bold">E se mesmo assim não responderem?</h2>
          <p className="mt-2 text-white/85">
            Espere alguns dias. Se o silêncio continuar, conte como foi no eaiRH — pode ser anônimo. Seu relato mostra a quem vai se
            candidatar depois como aquela empresa trata quem participa do processo, e a empresa pode responder no mesmo lugar.
          </p>
          <button type="button" onClick={() => openComplaint(f.empresa.trim() ? { empresa: f.empresa.trim() } : undefined)}
            className="mt-4 rounded-xl bg-orange-600 hover:bg-orange-700 px-5 py-2.5 font-semibold">
            Contar como foi o meu processo
          </button>
        </section>

        <p className="mt-8 text-sm text-gray-500">
          Antes de se candidatar, vale ver <Link to="/guia/como-saber-se-uma-empresa-da-retorno" className="text-primary underline">como saber se uma empresa dá retorno</Link>.
        </p>
      </main>
    </div>
  );
}
